
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Shared by Feature C (3-akset laser) and Feature D (korteste afstand) -
// both need "the point cloud's actual points near a location" and neither
// wants to duplicate octree-walking code. Deliberately reads only whatever
// octree nodes are *already loaded* (resident in memory because the user
// has navigated/rendered that area) rather than triggering a fresh LOD
// fetch mid-computation - a first-version simplification the spec's own
// framing of this whole sprint anticipates ("første version"), noted
// explicitly here rather than silently. In practice this is rarely a real
// limitation: a user pointing a laser or requesting a shortest-distance
// measurement is looking at the area in question, which Potree has
// therefore already streamed in at the current view's LOD.
const scratchVec = new THREE.Vector3();

function nodeWorldBox(pointcloud, node){
	if(!node.boundingBox){
		return null;
	}
	return node.boundingBox.clone().applyMatrix4(pointcloud.matrixWorld);
}

// A geometry node's "position" attribute holds coordinates relative to that
// node's own boundingBox.min (see PointCloudOctree.toTreeNode, which places
// the rendered scene node at exactly that offset) - so pointcloud.matrixWorld
// alone only lands correctly for a node whose min is the origin (the root).
// Every other node's points are shifted by its own min without this, which
// silently dropped most points from every query below (a clip-box export
// returned only a sparse coarse-LOD sliver of what was on screen).
export function nodeToWorldMatrix(pointcloud, node){
	let nodeMatrix = new THREE.Matrix4().makeTranslation(...node.boundingBox.min.toArray());
	return new THREE.Matrix4().multiplyMatrices(pointcloud.matrixWorld, nodeMatrix);
}

// Walks every loaded octree node overlapping `box` (world space) and calls
// onNode(node, nodeWorldBox) for each - callers extract whatever point
// data they need themselves (positions only, or positions is normally all
// there is to work with here). Kept as a single traversal so both
// "collect all points" and "collect points near a ray" (a much tighter
// filter, applied by the caller) share the exact same node-selection logic.
export function forEachLoadedNodeInBox(pointcloud, box, onNode){
	let root = pointcloud.pcoGeometry && pointcloud.pcoGeometry.root;
	if(!root){
		return;
	}

	let stack = [root];
	while(stack.length > 0){
		let node = stack.pop();
		if(!node){
			continue;
		}

		let worldBox = nodeWorldBox(pointcloud, node);
		if(worldBox && !worldBox.intersectsBox(box)){
			continue;
		}

		if(node.geometry && node.geometry.attributes && node.geometry.attributes.position){
			onNode(node, worldBox);
		}

		// PointCloudOctreeGeometryNode keeps children as a plain object keyed
		// by octant index ({0: node, 3: node, ...}), not a dense array - the
		// rendered PointCloudOctreeNode wrapper (elsewhere in Potree) uses a
		// real array, but pcoGeometry's own geometry-node tree (which is
		// what carries loaded point data) does not.
		for(let child of Object.values(node.children || {})){
			if(child){
				stack.push(child);
			}
		}
	}
}

// World-space point positions (plain {x,y,z} objects) from every loaded
// node overlapping `box`, further filtered to those actually inside it.
export function collectLoadedPointsInBox(pointcloud, box){
	let results = [];
	forEachLoadedNodeInBox(pointcloud, box, (node) => {
		let posAttr = node.geometry.attributes.position;
		let matrix = nodeToWorldMatrix(pointcloud, node);
		for(let i = 0; i < posAttr.count; i++){
			scratchVec.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
			if(box.containsPoint(scratchVec)){
				results.push({x: scratchVec.x, y: scratchVec.y, z: scratchVec.z});
			}
		}
	});
	return results;
}

// Points within `radius` of an infinite ray's own line (not clipped to the
// ray's forward direction only - a "cylinder around the line" test, per
// Feature C's "lokal nabosøgning omkring strålen"), each tagged with its
// signed distance *along* the ray direction from the origin (so the caller
// can find the nearest one specifically in front of the origin).
export function collectLoadedPointsNearRay(pointcloud, rayOrigin, rayDirection, radius, maxDistance){
	let dir = rayDirection.clone().normalize();
	let box = new THREE.Box3().setFromCenterAndSize(
		rayOrigin.clone().addScaledVector(dir, maxDistance / 2),
		new THREE.Vector3(maxDistance + radius * 2, maxDistance + radius * 2, maxDistance + radius * 2),
	);
	// A generous cube around the whole ray segment is simpler and safe -
	// the per-point cylinder test below is what actually matters for
	// correctness; this box only decides which octree nodes are worth
	// walking at all.

	let results = [];
	let toPoint = new THREE.Vector3();
	forEachLoadedNodeInBox(pointcloud, box, (node) => {
		let posAttr = node.geometry.attributes.position;
		let matrix = nodeToWorldMatrix(pointcloud, node);
		for(let i = 0; i < posAttr.count; i++){
			scratchVec.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
			toPoint.subVectors(scratchVec, rayOrigin);
			let along = toPoint.dot(dir);
			if(along < 0 || along > maxDistance){
				continue;
			}
			let perp = toPoint.clone().addScaledVector(dir, -along);
			if(perp.length() <= radius){
				results.push({point: scratchVec.clone(), along});
			}
		}
	});
	return results;
}

// World-space AABB of a Potree.BoxVolume (Volume.js: a plain -0.5..0.5 unit
// cube, scaled/rotated/translated by its own Object3D transform) - the 8
// local corners transformed by matrixWorld, used only to cheaply decide
// which octree nodes are worth a precise per-point test at all.
export function boxVolumeWorldAABB(volume){
	volume.updateMatrixWorld(true);
	let box = new THREE.Box3();
	let corner = new THREE.Vector3();
	for(let x of [-0.5, 0.5]){
		for(let y of [-0.5, 0.5]){
			for(let z of [-0.5, 0.5]){
				corner.set(x, y, z).applyMatrix4(volume.matrixWorld);
				box.expandByPoint(corner);
			}
		}
	}
	return box;
}

// Points from every loaded octree node overlapping `volume`'s world AABB,
// further filtered to those actually inside its oriented box (it can be
// rotated/scaled, unlike a plain axis-aligned Box3 - see
// boxVolumeWorldAABB), together with each point's current color normalized
// to 0-1 (LASExporter.toLAS's own expected range). Backs the box-section
// "Eksportér til LAS" button: exports exactly the points a SHOW_INSIDE clip
// on this same volume would keep visible.
export function collectLoadedPointsInVolumeWithColor(pointcloud, volume){
	let inverse = new THREE.Matrix4().copy(volume.matrixWorld).invert();
	let worldBox = boxVolumeWorldAABB(volume);
	let local = new THREE.Vector3();

	let positions = [];
	let colors = [];

	forEachLoadedNodeInBox(pointcloud, worldBox, (node) => {
		let posAttr = node.geometry.attributes.position;
		let rgba = node.geometry.attributes.rgba;
		let matrix = nodeToWorldMatrix(pointcloud, node);
		for(let i = 0; i < posAttr.count; i++){
			scratchVec.fromBufferAttribute(posAttr, i).applyMatrix4(matrix);
			local.copy(scratchVec).applyMatrix4(inverse);
			if(Math.abs(local.x) > 0.5 || Math.abs(local.y) > 0.5 || Math.abs(local.z) > 0.5){
				continue;
			}

			positions.push(scratchVec.x, scratchVec.y, scratchVec.z);
			if(rgba){
				colors.push(
					rgba.array[i * 4 + 0] / 255,
					rgba.array[i * 4 + 1] / 255,
					rgba.array[i * 4 + 2] / 255,
					1);
			}else{
				colors.push(1, 1, 1, 1);
			}
		}
	});

	return {positions, colors};
}
